'use client'

import { Image, OutlineButton, Text } from '@macarongfactory/web-ui'
import { useRef } from 'react'

import { logEvent } from '@/shared/external/firebase/analytics'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'

import {
  MYCLE_HOME_URL,
  PLACE_DETAIL_PROMOTION_BANNER_IMAGE_URL,
  PLACE_DETAIL_PROMOTION_BANNER_ONELINK_URL,
} from '../../lib'
import { useImpressionLog } from '../../model'

const PROMOTION_BANNER_ALT = '마이클 앱 다운받고 2만원 쿠폰 받기'
// 우측 일러스트 원본 비율 (480 x 360 = 4:3)
// 배너 안에서 그리는 크기 (원본 480×360)
const BANNER_IMAGE_WIDTH_PX = 148
const BANNER_IMAGE_HEIGHT_PX = 111
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)'
// Figma: linear-gradient(-60.01deg, #0D3E51 41.765%, #2D3741 58.235%) — DS 토큰 없어 값 그대로
const BANNER_BACKGROUND =
  'linear-gradient(-60.011270022311834deg, rgb(13, 62, 81) 41.765%, rgb(45, 55, 65) 58.235%)'

const SECTION_IMPRESSION_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.PROMOTION_BANNER,
  type: TYPE.SECTION,
  label: LABELS.COMMON_WEB_PLACE.PROMOTION_BANNER,
}

interface IPlacePromotionBannerProps {
  isPartnerEntry?: boolean
}

const PlacePromotionBanner = ({
  isPartnerEntry,
}: IPlacePromotionBannerProps) => {
  const bannerRef = useRef<HTMLDivElement | null>(null)
  // 어노테이션: 프로모션 배너는 항상 자기 onelink(oligd48b) 사용.
  // 목록에서 넘어온 ?seoOnelink= 로 덮어쓰지 않도록 useSeoOnelinkUrl을 쓰지 않는다.
  const onelinkUrl = PLACE_DETAIL_PROMOTION_BANNER_ONELINK_URL

  useImpressionLog({
    ref: bannerRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.SECTION.SHOW,
    params: SECTION_IMPRESSION_PARAMS,
    enabled: !isPartnerEntry,
  })

  // 어노테이션: 터치 영역 배너 전체 / 모바일 → 원링크, 데스크탑 → mycle.co.kr
  // <a> 오버레이와 OutlineButton 양쪽에서 동일 동작하도록 직접 이동시킨다.
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, {
      code: CODE.COMMON_WEB_PLACE.PROMOTION_BANNER,
      type: TYPE.BUTTON,
      label: LABELS.COMMON_WEB_PLACE.PROMOTION_BUTTON,
    })
    const isDesktop = window.matchMedia(DESKTOP_MEDIA_QUERY).matches
    window.open(
      isDesktop ? MYCLE_HOME_URL : onelinkUrl,
      '_blank',
      'noopener,noreferrer'
    )
  }

  // 제휴 유입은 배너 영역 자체를 노출하지 않는다 (WEB-5901)
  if (isPartnerEntry) return null

  return (
    <div
      ref={bannerRef}
      className='relative isolate flex w-full items-center gap-dimension-x4 px-dimension-x6'
      style={{ backgroundImage: BANNER_BACKGROUND }}
    >
      {/* 배너 전체가 터치 영역: 콘텐츠는 pointer-events-none으로 클릭을 이 링크로 흘려보낸다 */}
      <a
        href={onelinkUrl}
        target='_blank'
        rel='noopener noreferrer'
        onClick={handleClick}
        aria-label={PROMOTION_BANNER_ALT}
        className='absolute inset-0'
      />
      <div className='pointer-events-none relative z-10 flex min-w-px flex-1 flex-col items-start justify-center gap-dimension-x2 py-dimension-x4'>
        <div className='flex flex-col items-start'>
          <Text
            variant='title-2-semibold'
            color='text-foreground-neutral-inverse'
          >
            마이클 앱 다운받고
          </Text>
          <Text variant='heading-3-bold' color='text-foreground-brand-strong'>
            2만원 쿠폰 받기
          </Text>
        </div>
        <OutlineButton
          text='다운받기'
          variant='neutral'
          size='small'
          clickable={handleClick}
          className='pointer-events-auto'
        />
      </div>
      <Image
        src={PLACE_DETAIL_PROMOTION_BANNER_IMAGE_URL}
        alt=''
        width={BANNER_IMAGE_WIDTH_PX}
        height={BANNER_IMAGE_HEIGHT_PX}
        radius={0}
        scaletype='fit'
        className='pointer-events-none relative z-10'
      />
    </div>
  )
}

export default PlacePromotionBanner
